import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { HtmlBlockContent, RenderMode } from '../../types';
import {
  HTML_BLOCK_FALLBACK_HEIGHT,
  HTML_BLOCK_IFRAME_SANDBOX,
  clampReportedHeight,
} from '../../helpers/block-types/html';

type Props = {
  block: { id: string; content: HtmlBlockContent };
  renderMode: RenderMode;
  measureElementRef?: (element: HTMLElement | null) => void;
  onUpdateBlockContent?: (blockId: string, content: Partial<HtmlBlockContent>) => void;
};

// Messages we accept from the embedded page. Anything else is ignored.
const HEIGHT_MESSAGE_TYPE = 'journey:html-block:height';

export const HtmlBlock = ({ block, renderMode, measureElementRef, onUpdateBlockContent }: Props) => {
  const { url, height } = block.content;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Start from the height persisted with the block so a returning viewer does
  // not see the frame jump from the fallback to the real height on every load.
  const [reportedHeight, setReportedHeight] = useState<number | null>(height ?? null);

  const effectiveHeight = reportedHeight ?? HTML_BLOCK_FALLBACK_HEIGHT;

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Identity is established by event.source, not by event.origin. The
      // sandbox omits allow-same-origin, so the frame has an opaque origin and
      // its messages arrive with event.origin === "null" — comparing that to
      // the serving origin rejected every height report. event.source is the
      // stronger check: it is this frame's own contentWindow, so no other
      // frame can satisfy it. See the matching note in HtmlArtifactFrame.
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) {
        return;
      }

      const data = event.data;
      if (!data || data.type !== HEIGHT_MESSAGE_TYPE) {
        return;
      }

      const next = clampReportedHeight(data.height);
      if (next === null) {
        return;
      }

      setReportedHeight((current) => {
        if (current === next) {
          return current;
        }
        // Persist so the height survives a reload. Editor only: a player-side
        // write would be a recipient mutating the sender's Journey.
        if (renderMode === 'editor' && onUpdateBlockContent) {
          onUpdateBlockContent(block.id, { height: next });
        }
        return next;
      });
    },
    [renderMode, onUpdateBlockContent, block.id]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  if (!url) {
    return null;
  }

  return (
    <div
      className='w-full'
      style={{ height: effectiveHeight }}
      ref={measureElementRef}
      data-testid='html-block'
    >
      <iframe
        ref={iframeRef}
        src={url}
        title={block.content.metadata?.name || 'Embedded HTML'}
        className={classNames('w-full h-full rounded-lg border-0', {
          // In the editor the frame would otherwise swallow clicks meant to
          // select the block. The player wants the opposite: the artifact is
          // the point, so it stays interactive there.
          'pointer-events-none': renderMode === 'editor',
        })}
        sandbox={HTML_BLOCK_IFRAME_SANDBOX}
        // Belt and braces alongside the sandbox: keep the artifact away from
        // device APIs it has no reason to reach.
        allow=''
        referrerPolicy='no-referrer'
        loading='lazy'
      />
    </div>
  );
};

export default HtmlBlock;
